/**
 * HealingSupportHub — Premium selection page for healing experiences
 */

import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Waves } from 'lucide-react';

const EXPERIENCES = [
  {
    id: 'spinal-ease',
    title: 'Spinal Ease',
    subtitle: 'Alignment, structure, safety',
    description: 'Gentle light flows through your spine, softening tension and creating space.',
    route: '/healing/spinal-ease',
    accent: 'rgba(200,180,120',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        {[4, 8, 12, 16, 20, 24].map(y => (
          <circle key={y} cx="14" cy={y} r="1.8" fill="rgba(220,195,140,0.7)" />
        ))}
        <line x1="14" y1="4" x2="14" y2="24" stroke="rgba(220,195,140,0.2)" strokeWidth="1" />
      </svg>
    ),
  },
  {
    id: 'heart-flow',
    title: 'Heart Flow',
    subtitle: 'Warmth, expansion, connection',
    description: 'A soft, warm light gently opens and holds your heart space.',
    route: '/healing/heart-flow',
    accent: 'rgba(210,170,130',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <circle cx="14" cy="13" r="6" fill="none" stroke="rgba(220,180,140,0.5)" strokeWidth="1.5" />
        <circle cx="14" cy="13" r="3" fill="rgba(230,190,145,0.4)" />
      </svg>
    ),
  },
  {
    id: 'womb-healing',
    title: 'Womb & Creative Centre',
    subtitle: 'Nurturing, grounding, creative flow',
    description: 'Deep warmth gathers in your centre, nurturing what lives within.',
    route: '/healing/womb-healing',
    accent: 'rgba(190,145,80',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <ellipse cx="14" cy="14" rx="8" ry="6" fill="none" stroke="rgba(210,165,95,0.5)" strokeWidth="1.5" />
        <ellipse cx="14" cy="14" rx="4" ry="3" fill="rgba(220,175,105,0.3)" />
      </svg>
    ),
  },
  {
    id: 'brain-clarity',
    title: 'Brain & Clarity',
    subtitle: 'Stillness, spaciousness, clear seeing',
    description: 'A soft light diffuses through the mind, bringing calm clarity.',
    route: '/healing/brain-clarity',
    accent: 'rgba(180,195,220',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <circle cx="14" cy="12" r="7" fill="none" stroke="rgba(190,205,230,0.5)" strokeWidth="1.2" />
        <circle cx="14" cy="12" r="3" fill="rgba(200,215,240,0.3)" />
        {[0, 60, 120, 180, 240, 300].map(a => (
          <line key={a} x1={14 + Math.cos(a * Math.PI / 180) * 7} y1={12 + Math.sin(a * Math.PI / 180) * 7}
            x2={14 + Math.cos(a * Math.PI / 180) * 10} y2={12 + Math.sin(a * Math.PI / 180) * 10}
            stroke="rgba(190,205,230,0.15)" strokeWidth="0.5" />
        ))}
      </svg>
    ),
  },
  {
    id: 'nervous-system',
    title: 'Nervous System Reset',
    subtitle: 'Calming, settling, regulation',
    description: 'A calm current flows through your entire system, settling every nerve.',
    route: '/healing/nervous-system',
    accent: 'rgba(170,195,135',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <line x1="14" y1="4" x2="14" y2="24" stroke="rgba(185,205,145,0.4)" strokeWidth="1.2" />
        <line x1="14" y1="10" x2="8" y2="16" stroke="rgba(185,205,145,0.3)" strokeWidth="0.8" />
        <line x1="14" y1="10" x2="20" y2="16" stroke="rgba(185,205,145,0.3)" strokeWidth="0.8" />
        <line x1="14" y1="20" x2="10" y2="26" stroke="rgba(185,205,145,0.2)" strokeWidth="0.8" />
        <line x1="14" y1="20" x2="18" y2="26" stroke="rgba(185,205,145,0.2)" strokeWidth="0.8" />
        {[4, 10, 14, 20].map(y => (
          <circle key={y} cx="14" cy={y} r="1.5" fill="rgba(195,215,155,0.4)" />
        ))}
      </svg>
    ),
  },
];

const BG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/85b307c0-58ce-40d0-8126-3abffae3b4a9/images/7b10f84fad84915141fbfafafafb56cfb9843f832f236ae9079caa5d5c99a51a.png';

export default function HealingSupportHub() {
  const navigate = useNavigate();

  return (
    <motion.div
      className="fixed inset-0 flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
      data-testid="healing-hub"
    >
      {/* Background */}
      <div className="fixed inset-0">
        <img src={BG_IMAGE} alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.4) saturate(0.6)' }} />
      </div>
      <div className="fixed inset-0" style={{
        background: 'linear-gradient(180deg, rgba(8,10,6,0.3) 0%, rgba(8,10,6,0.5) 30%, rgba(8,10,6,0.85) 100%)',
      }} />

      {/* Back */}
      <div className="relative z-10 px-6 pt-6">
        <motion.button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/private-suite'))}
          className="p-2.5 rounded-full"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)' }}
          whileTap={{ scale: 0.92 }}
          data-testid="hub-back-btn"
        >
          <ArrowLeft size={18} className="text-white/40" />
        </motion.button>
      </div>

      {/* Title */}
      <div className="relative z-10 px-8 pt-8 pb-6 text-center">
        <motion.h1
          className="text-3xl sm:text-4xl text-white/80 mb-2"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 1 }}
        >
          Health Support
        </motion.h1>
        <motion.p
          className="text-white/30 text-sm mb-5"
          style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.04em' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          Where would you like to bring softness today?
        </motion.p>
        {/* Ease-a-flare-up shortcut — always one tap away, no matter which
            healing experience the user came here for. */}
        <motion.button
          onClick={() => navigate('/healing/spinal-ease/emergency')}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs"
          style={{
            background: 'rgba(180,220,255,0.08)',
            border: '1px solid rgba(180,220,255,0.25)',
            color: 'rgba(220,240,255,0.9)',
            fontFamily: "'Manrope', sans-serif",
            letterSpacing: '0.05em',
          }}
          data-testid="hub-emergency-chip"
        >
          <Waves size={12} />
          Ease a flare-up now · 3 min
        </motion.button>
      </div>

      {/* Experience Cards */}
      <div className="relative z-10 flex-1 overflow-y-auto px-6 pb-12 pt-4">
        <div className="flex flex-col items-center gap-4 py-4">
        {EXPERIENCES.map((exp, i) => (
          <motion.button
            key={exp.id}
            onClick={() => navigate(exp.route)}
            className="w-full max-w-sm rounded-2xl overflow-hidden text-left relative"
            style={{
              background: `linear-gradient(135deg, rgba(20,18,12,0.9) 0%, rgba(25,22,15,0.85) 100%)`,
              border: `1px solid ${exp.accent},0.12)`,
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + i * 0.2, duration: 0.8 }}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            data-testid={`hub-card-${exp.id}`}
          >
            {/* Slow glow pulse behind card */}
            <motion.div
              className="absolute inset-0 rounded-2xl pointer-events-none"
              animate={{
                boxShadow: [
                  `0 0 15px 2px ${exp.accent},0.04)`,
                  `0 0 30px 6px ${exp.accent},0.1)`,
                  `0 0 15px 2px ${exp.accent},0.04)`,
                ],
              }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            />
            <div className="p-5 relative z-10">
              {/* Icon + Title */}
              <div className="flex items-center gap-3 mb-2">
                {exp.icon}
                <div>
                  <h2 className="text-lg text-white/80" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                    {exp.title}
                  </h2>
                  <p className="text-white/30 text-xs" style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.04em' }}>
                    {exp.subtitle}
                  </p>
                </div>
              </div>

              {/* Description */}
              <p className="text-white/25 text-sm leading-relaxed mt-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {exp.description}
              </p>

              {/* Subtle accent line */}
              <div className="mt-4 h-px" style={{ background: `linear-gradient(90deg, transparent, ${exp.accent},0.15), transparent)` }} />
            </div>
          </motion.button>
        ))}
        </div>
      </div>
    </motion.div>
  );
}
